import { DEV_URL, rget, rpost, rdelete } from 'utils/requests';

const ENDPOINTS = {
    commodities: 'GetCommodities',
    dimensions: 'GetDimensions',
    'units-of-measurement': 'GetUnitOfMeasurements',
    zones: 'GetZones',
    'load-zones': 'GetLoadZones',
    'billing-types': 'GetBillingTypes',
    'weather-zones': 'GetWeatherZones',
    'utility-billing-types': 'GetUtilityBillingTypes',
    'state-commodities': 'GetStateCommodities',
    'license-types': 'GetLicenseTypes',
    'state-license-requirements': 'GetStateLicenseRequirements',
    'operational-states': 'GetLookupStates',
    'operational-utilities': 'GetLookupUtilities',
};

export const readField = (value, key) => value?.[key] ?? value?.[key[0].toUpperCase() + key.slice(1)];

export const getLookupPage = async (entity, { pageIndex, pageSize, search, sortBy, sortDirection }, signal) => {
    const endpoint = ENDPOINTS[entity];
    if (!endpoint) throw new Error('This lookup is not connected yet.');
    const query = new URLSearchParams({
        PageNumber: String(pageIndex + 1), PageSize: String(pageSize),
        SortBy: sortBy || 'Id', SortDirection: sortDirection || 'asc',
    });
    if (search) query.set('Search', search);
    const response = await rget(DEV_URL.replace(/\/$/, '') + '/api/Lookup/' + endpoint + '?' + query, { signal });
    const data = checkMutationResponse(response, 'Unable to load lookup records. Please try again.');
    const items = readField(data, 'items');
    const total = readField(data, 'totalRecords');
    if (!Array.isArray(items) || !Number.isInteger(total) || total < 0) {
        throw new Error('The lookup response could not be read. Please try again.');
    }
    // Explicit columns control rendering; identifiers are never inferred as columns.
    return { items, total };
};

const RECORD_NAMES = {
    commodities: 'Commodity', dimensions: 'Dimension', 'units-of-measurement': 'UnitOfMeasurement', zones: 'Zone',
    'load-zones': 'LoadZone', 'billing-types': 'BillingType', 'weather-zones': 'WeatherZone', 'utility-billing-types': 'UtilityBillingType',
    'state-commodities': 'StateCommodity', 'license-types': 'LicenseType', 'state-license-requirements': 'StateLicenseRequirement',
};
const recordUrl = (entity, operation, id) => {
    const name = RECORD_NAMES[entity];
    if (!name) throw new Error('This lookup is not connected yet.');
    // These endpoints use the GUID returned in the list's id field.
    const validId = ['load-zones', 'zones', 'billing-types', 'utility-billing-types', 'commodities', 'dimensions', 'units-of-measurement', 'license-types', 'state-commodities', 'state-license-requirements'].includes(entity)
        ? typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
            && id !== '00000000-0000-0000-0000-000000000000'
        : Number.isInteger(Number(id)) && Number(id) > 0;
    if (operation !== 'Create' && !validId) throw new Error('The record ID is missing or invalid. Refresh the list and try again.');
    return DEV_URL.replace(/\/$/, '') + '/api/Lookup/' + operation + name + (operation === 'Get' ? 'ById' : '') + (id == null ? '' : '?id=' + encodeURIComponent(id));
};
const checkMutationResponse = (response, fallback = 'The operation failed. Please try again.') => {
    const body = response?.data;
    if (!response || response.status < 200 || response.status >= 300 || readField(body, 'isSuccess') !== true) {
        if (response?.status === 401) throw new Error('Your session has expired. Please sign in again.');
        if (response?.status === 403) throw new Error('You do not have permission to manage these records.');
        if (response?.status >= 500) throw new Error('The service is unavailable. Please try again later.');
        const errors = readField(body, 'errors');
        const details = errors && typeof errors === 'object' ? Object.values(errors).flat().filter(value => typeof value === 'string').join(' ') : '';
        const message = readField(body, 'message') || readField(body, 'detail') || readField(body, 'title');
        throw new Error(details || (typeof message === 'string' && message) || fallback);
    }
    return readField(body, 'data');
};
export const getLookupRecord = async (entity, id, signal) => {
    const record = checkMutationResponse(await rget(recordUrl(entity, 'Get', id), { signal }));
    if (!record || typeof record !== 'object' || Array.isArray(record)) throw new Error('The record could not be loaded.');
    return record;
};
const successMessage = response => {
    const message = readField(response?.data, 'message');
    return typeof message === 'string' ? message.trim() : '';
};
export const saveLookupRecord = async (entity, values, id) => {
    const response = await rpost(recordUrl(entity, id == null ? 'Create' : 'Update', id), values);
    return { data: checkMutationResponse(response), message: successMessage(response) };
};
export const deleteLookupRecord = async (entity, id) => {
    const response = await rdelete(recordUrl(entity, 'Delete', id));
    const result = checkMutationResponse(response);
    if (result !== true) throw new Error('The record was not deleted. Refresh the list and try again.');
    return { data: result, message: successMessage(response) };
};
